import { asyncOperation, clientRepo, useCollectionBind, useUser } from "@zickko/client";
import { DraftEmail, EmailKey, firestorePaths } from "@zickko/shared";
import { useOrg } from "../providers/OrgProvider";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Clock3, CornerUpLeft, CornerUpRight, EllipsisVerticalIcon, ReplyAll, Trash2 } from "lucide-react";
import { DraftsEmailListWidget } from "../widgets/email/DraftsEmailListWidget";
import { ComposeWidget, Mode } from "../widgets/ComposeWidget";

export const DraftsPage = () => {
  const { org } = useOrg();
  const { user } = useUser();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEmail, setSelectedEmail] = useState<DraftEmail | null>(null);
  const emailId = searchParams.get("emailId");

  const { list: emailList = [] } = useCollectionBind(firestorePaths.drafts, {
    key: {
      orgId: org.id,
      uid: user.uid,
    },
    orderByField: "lastModified",
    orderByDirection: "desc",
    limit: 10,
  });

  useEffect(() => {
    if (!selectedEmail && emailList.length > 0) {
      const firstEmail = emailList[0];
      setSearchParams({ emailId: firstEmail.id });
      setSelectedEmail(firstEmail);
    } else if (selectedEmail) {
      setSearchParams({ emailId: selectedEmail.id });
    }
  }, [selectedEmail, emailList, setSearchParams, setSelectedEmail]);

  const onDeleteEmail = () => {
    if (!selectedEmail) return;

    asyncOperation({
      fn: () => {
        const emailKey: EmailKey = {
          emailId: selectedEmail.id,
          orgId: selectedEmail.orgId,
          uid: selectedEmail.uid,
        };
        return clientRepo.draftEmail.remove(emailKey);
      },
      onSuccess: () => {
        toast.success("Draft Deleted Successfully");
        setSelectedEmail(null); // Reset selection after deletion
      },
      onError(e) {
        toast.error("Error in deleting draft.");
      },
    });
  };

  return (
    <div className="inbox-page">
      <div className="email-list-widget">
        <div className="email-list-header">
          <div className="inbox-heading">Drafts</div>
          <div className="switch-button">
            <div className={`all-mail`}>All Mail</div>
          </div>
        </div>
        <DraftsEmailListWidget emailList={emailList} onSelectEmail={(email) => setSelectedEmail(email)} />
      </div>
      <div className="email-view-widget">
        {emailList.length === 0 ? (
          <div className="no-drafts-message">No drafts to see</div>
        ) : (
          <>
            <div className="email-action-items">
              <div className="actions">
                <Trash2 onClick={onDeleteEmail} size={18} />
                <div className="vertical-line"></div>
                <Clock3 size={18} />
              </div>
              <div className="reply-actions">
                <CornerUpLeft size={18} />
                <ReplyAll size={18} />
                <CornerUpRight size={18} />
                <div className="vertical-line"></div>
                <EllipsisVerticalIcon size={18} />
              </div>
            </div>
            <ComposeWidget mode={Mode.Drafts} currentDraft={selectedEmail} />
          </>
        )}
      </div>
    </div>
  );
};
